/**
 * UNE JOURNÉE : SON NOM, ET SES VOISINES — module PUR (RQ, 05/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Quand je suis sur un calendrier mensuel, quand je clique sur une journée,
 * je veux la voir en mode jour. »
 *
 * Le mode jour a besoin de deux choses qu'aucun module ne portait : écrire la
 * date en toutes lettres (« lundi 5 octobre 2026 » — un titre de fenêtre ne
 * peut pas dire « 2026-10-05 »), et passer au jour suivant ou précédent.
 *
 * ── ⚠️ POURQUOI TOUT SE CALCULE SUR LES CHAÎNES ───────────────────────────
 *
 * `new Date('2026-10-05')` est interprété en UTC, puis relu dans le fuseau du
 * navigateur : à Tunis (UTC+1) cela tombe le 5 à 01 h, mais à l'ouest de
 * Greenwich cela tombe LA VEILLE. Un `getDay()` sur cette base rend alors le
 * mauvais jour de la semaine, et un `setDate(+1)` franchissant un changement
 * d'heure saute ou répète un jour.
 *
 * Le projet connaît déjà ce piège (les écrans qui manipulent des jours nus
 * passent par `T12:00:00` pour s'en garder). On va plus loin ici : AUCUN objet
 * `Date` n'est construit. Tout passe par un NUMÉRO DE JOUR CONTINU (jours
 * juliens) : le décalage est une addition, et le jour de la semaine ce numéro
 * modulo 7. Le résultat ne dépend donc ni du fuseau, ni de l'heure d'été, ni de
 * l'horloge — ce qui le rend éprouvable, et le test le relit sous QUATRE
 * fuseaux, dont UTC+14 et UTC−11.
 *
 * ⚠️ MODULE SANS AUCUN IMPORT (feuille) : compilé et éprouvé par `npm test`
 * (préfixe `front/`).
 */

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Le jour est-il une date ISO lisible ? Un `AAAA-MM-JJ` et rien d'autre. */
export const jourValide = (iso: string): boolean => ISO.test(String(iso || ''));

/** Numéro de jour continu (jours juliens modifiés), SANS objet `Date`.
 *  Algorithme de Fliegel–Van Flandern : exact sur tout le calendrier
 *  grégorien, années bissextiles et séculaires comprises. */
function numeroDeJour(a: number, m: number, j: number): number {
  const a2 = m <= 2 ? a - 1 : a;
  const m2 = m <= 2 ? m + 12 : m;
  const siecle = Math.floor(a2 / 100);
  const bis = Math.floor(siecle / 4) - siecle;
  return Math.floor(365.25 * a2) + Math.floor(30.6001 * (m2 + 1)) + j + bis + 1_720_996;
}

function depuisNumero(n: number): { a: number; m: number; j: number } {
  let l = n + 68_569 + 1;
  const nn = Math.floor((4 * l) / 146_097);
  l -= Math.floor((146_097 * nn + 3) / 4);
  const i = Math.floor((4000 * (l + 1)) / 1_461_001);
  l = l - Math.floor((1461 * i) / 4) + 31;
  const k = Math.floor((80 * l) / 2447);
  const j = l - Math.floor((2447 * k) / 80);
  l = Math.floor(k / 11);
  const m = k + 2 - 12 * l;
  const a = 100 * (nn - 49) + i + l;
  return { a, m, j };
}

const deux = (n: number) => String(n).padStart(2, '0');

/** Le jour décalé de `n` jours (négatif = en arrière). Rend `''` si la date
 *  n'est pas lisible — jamais une date inventée : un « jour suivant » calculé
 *  depuis rien ferait naviguer vers un jour qui n'a pas été demandé. */
export function jourPlus(iso: string, n: number): string {
  const m = ISO.exec(String(iso || ''));
  if (!m) return '';
  const { a, m: mo, j } = depuisNumero(numeroDeJour(Number(m[1]), Number(m[2]), Number(m[3])) + n);
  return `${String(a).padStart(4, '0')}-${deux(mo)}-${deux(j)}`;
}

/** Le jour de la semaine, 0 = dimanche (comme `Date.getDay`), ou -1. */
export function jourDeSemaine(iso: string): number {
  const m = ISO.exec(String(iso || ''));
  if (!m) return -1;
  /* ⚠️ `+2`, ET LE CHIFFRE A ÉTÉ MESURÉ, PAS DEVINÉ. Un `+1` posé au jugé
     décalait TOUT d'un jour : le 5 octobre 2026 (un lundi) sortait « dimanche »,
     et le titre de la fenêtre aurait annoncé le mauvais jour sans que rien ne
     le signale. La constante est calée sur trois faits vérifiables, que le test
     rejoue (`front-jour`) : lundi 05/10/2026, jeudi 01/01/2026, dimanche
     04/10/2026. Ne pas y toucher sans refaire ces trois-là. */
  return (numeroDeJour(Number(m[1]), Number(m[2]), Number(m[3])) + 2) % 7;
}

/**
 * « lundi 5 octobre 2026 » — le titre d'une fenêtre de journée.
 *
 * ⚠️ Pas de `toLocaleDateString` : il dépend de la locale du navigateur, et
 * l'application est en français seulement (`ged` et `eil`). Sur un poste
 * configuré en anglais, le titre serait sorti en anglais au milieu d'un écran
 * français. Les deux listes de noms sont donc ici, et elles sont testées.
 *
 * `avecJourSemaine = false` rend « 5 octobre 2026 », pour un sous-titre où le
 * nom du jour est déjà écrit à côté.
 */
export function libelleJourLong(iso: string, avecJourSemaine = true): string {
  const m = ISO.exec(String(iso || ''));
  if (!m) return '';
  const jour = Number(m[3]);
  const mois = MOIS[Number(m[2]) - 1];
  if (!mois) return '';
  /* « 1er », et non « 1 » : c'est la forme française, et elle se lit. */
  const n = jour === 1 ? '1er' : String(jour);
  const corps = `${n} ${mois} ${m[1]}`;
  return avecJourSemaine ? `${JOURS[jourDeSemaine(iso)]} ${corps}` : corps;
}

/**
 * « octobre 2026 » — le titre d'un mois, à partir de « 2026-10 » (ou d'un jour
 * « 2026-10-15 »). `avecAnnee = false` rend « octobre ». Vide si la valeur n'est
 * pas un mois : mieux vaut un titre absent qu'un « Invalid Date ».
 *
 * Comme `libelleJourLong`, il n'appelle pas `toLocaleDateString` : le nom du mois
 * ne doit pas dépendre de la langue du navigateur.
 */
export function libelleMois(m?: string | null, avecAnnee = true): string {
  const r = /^(\d{4})-(\d{2})/.exec(String(m || ''));
  if (!r) return '';
  const nom = MOIS[Number(r[2]) - 1];
  if (!nom) return '';
  return avecAnnee ? `${nom} ${r[1]}` : nom;
}

/** « lun. 05/10 » — pour une étiquette étroite. */
export function libelleJourCourt(iso: string): string {
  const m = ISO.exec(String(iso || ''));
  if (!m) return '';
  return `${JOURS[jourDeSemaine(iso)].slice(0, 3)}. ${m[3]}/${m[2]}`;
}

/** Samedi ou dimanche. ⚠️ N'EST PAS « le laboratoire est fermé » : il ouvre le
 *  samedi, et travaille les dimanches et les jours fériés. C'est un repère
 *  d'affichage, rien de plus. */
export function finDeSemaine(iso: string): boolean {
  const d = jourDeSemaine(iso);
  return d === 0 || d === 6;
}
